<template>
  <div class="main-content">
    <!-- Header -->
    <div class="row mb-3">
      <div class="col-md-12">
        <div class="card">
          <div
            class="card-body d-flex align-items-center justify-content-between py-2"
          >
            <div>
              <h4 class="mb-0">
                <i class="fas fa-code-branch text-primary mr-2"></i>
                Releases —
                <span class="text-muted">{{ project.name || "Sistema" }}</span>
              </h4>
            </div>
            <div>
              <button class="btn btn-secondary mr-2" @click="$router.back()">
                <i class="fas fa-arrow-left mr-1"></i> Voltar
              </button>
              <router-link
                :to="`/projects/${projectId}/releases/add`"
                class="btn btn-primary"
              >
                <i class="fas fa-plus mr-1"></i> Nova Release
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
            </div>
            <div v-else>
              <table
                class="table table-hover table-bordered"
                v-if="releases.length > 0"
              >
                <thead class="thead-light">
                  <tr>
                    <th>Versão</th>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Início dos Testes</th>
                    <th>Fechamento</th>
                    <th>Chamados</th>
                    <th class="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="release in releases" :key="release.id">
                    <td>
                      <strong>{{ release.version_string }}</strong>
                    </td>
                    <td>{{ release.name }}</td>
                    <td>
                      <span :class="statusBadge(release.status)">
                        {{ statusLabel(release.status) }}
                      </span>
                      <span
                        v-if="isAlertStatus(release.status)"
                        class="ml-1"
                        :title="alertTitle(release.status)"
                      >
                        <i :class="alertIcon(release.status)"></i>
                      </span>
                    </td>
                    <td>{{ release.data_prevista_inicio_testes_f || "—" }}</td>
                    <td>{{ release.data_fechamento_release_f || "—" }}</td>
                    <td class="text-center">
                      <span class="badge badge-info">{{
                        release.tickets_count || 0
                      }}</span>
                    </td>
                    <td class="text-center" style="min-width: 140px">
                      <router-link
                        :to="`/projects/${projectId}/releases/view/${release.id}`"
                        class="btn btn-sm btn-crud view"
                        title="Visualizar"
                      >
                        <i class="fas fa-eye"></i>
                      </router-link>
                      <router-link
                        v-if="isAdmin"
                        :to="`/projects/${projectId}/releases/edit/${release.id}`"
                        class="btn btn-sm btn-crud edit ml-1"
                        title="Editar"
                      >
                        <i class="fas fa-pencil-alt"></i>
                      </router-link>
                      <button
                        class="btn btn-sm btn-crud view ml-1"
                        title="Gerar PDF"
                        @click="downloadPdf(release)"
                      >
                        <i class="fas fa-file-pdf"></i>
                      </button>
                      <button
                        v-if="isAdmin"
                        class="btn btn-sm btn-crud delete ml-1"
                        title="Excluir"
                        @click="destroy(release)"
                      >
                        <i class="far fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="text-center py-4 text-muted">
                <i class="fas fa-code-branch fa-3x mb-2"></i>
                <p>Nenhuma release cadastrada para este sistema.</p>
                <router-link
                  :to="`/projects/${projectId}/releases/add`"
                  class="btn btn-primary btn-sm"
                >
                  Criar primeira release
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      releases: [],
      project: {},
      loading: true,
    };
  },

  computed: {
    projectId() {
      return this.$route.params.project_id;
    },
    isAdmin() {
      return this.$store.state.user.perfil_id === 8;
    },
  },

  mounted() {
    this.getProject();
    this.getReleases();
  },

  methods: {
    getProject() {
      axios
        .get(this.$store.state.api + `projects/${this.projectId}`)
        .then((res) => {
          this.project = res.data;
        })
        .catch(() => {});
    },

    getReleases() {
      this.loading = true;
      axios
        .get(
          this.$store.state.api +
            `releases?project_id=${this.projectId}&paginated=false&orderByDesc=id&with[]=project&withCount[]=tickets`
        )
        .then((res) => {
          this.releases = res.data.data || res.data;
        })
        .catch(() => {
          this.$message(
            "Erro",
            "Não foi possível carregar as releases.",
            "error"
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    statusLabel(status) {
      const map = {
        planejada: "Planejada",
        em_desenvolvimento: "Em Desenvolvimento",
        em_testes: "Em Testes",
        fechada: "Fechada",
        finalizada: "Finalizada",
        planejamento_atrasado: "Planejamento Atrasado",
        desenvolvimento_atrasado: "Desenvolvimento Atrasado",
        aguardando_testes: "Aguardando Testes",
        testes_atrasados: "Testes Atrasados",
        aguardando_aprovacao_qa: "Aguardando Aprovação QA",
        aguardando_deploy: "Aguardando Deploy",
        deploy_atrasado: "Deploy Atrasado",
      };
      return map[status] || status;
    },

    statusBadge(status) {
      const map = {
        planejada: "badge badge-secondary",
        em_desenvolvimento: "badge badge-primary",
        em_testes: "badge badge-warning",
        fechada: "badge badge-success",
        finalizada: "badge badge-info",
        planejamento_atrasado: "badge badge-danger",
        desenvolvimento_atrasado: "badge badge-danger",
        aguardando_testes: "badge badge-info",
        testes_atrasados: "badge badge-danger",
        aguardando_aprovacao_qa: "badge badge-warning",
        aguardando_deploy: "badge badge-primary",
        deploy_atrasado: "badge badge-danger",
      };
      return map[status] || "badge badge-secondary";
    },

    isAlertStatus(status) {
      return ["planejamento_atrasado", "desenvolvimento_atrasado", "testes_atrasados", "deploy_atrasado", "aguardando_aprovacao_qa"].includes(status);
    },

    alertIcon(status) {
      if (["desenvolvimento_atrasado", "testes_atrasados", "deploy_atrasado"].includes(status)) {
        return "fas fa-exclamation-triangle text-danger";
      }
      if (status === "aguardando_aprovacao_qa") return "fas fa-user-clock text-warning";
      return "";
    },

    alertTitle(status) {
      const map = {
        desenvolvimento_atrasado: "Atenção: desenvolvimento não concluído no prazo de testes",
        testes_atrasados: "Atenção: prazo de testes expirado",
        deploy_atrasado: "Atenção: deploy não realizado no prazo previsto",
        aguardando_aprovacao_qa: "Aguardando aprovação do QA",
      };
      return map[status] || "";
    },

    downloadPdf(release) {
      axios
        .get(this.$store.state.api + `releases/${release.id}/pdf`, {
          responseType: "blob",
        })
        .then((res) => {
          const disposition = res.headers["content-disposition"] || "";
          const match = disposition.match(
            /filename[^;=\n]*=(['"]?)([^'"\n;]+)\1/
          );
          const fileName = match
            ? match[2]
            : `release_${release.id}_${release.version_string}.pdf`;
          const blob = new Blob([res.data], { type: "application/pdf" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .catch(() => {
          this.$message("Erro", "Não foi possível gerar o PDF.", "error");
        });
    },

    destroy(release) {
      swal({
        title: "Deseja excluir esta release?",
        text: "Essa operação não pode ser desfeita.",
        type: "warning",
        buttons: {
          cancel: { text: "Não", value: "cancel", className: "btn-danger" },
          confirm: { text: "Sim", value: "confirm" },
        },
      }).then((value) => {
        if (value === "confirm") {
          axios
            .delete(this.$store.state.api + `releases/${release.id}`)
            .then(() => {
              swal("Sucesso", "Release excluída com sucesso!", "success");
              this.getReleases();
            })
            .catch(() => {
              swal("Erro", "Não foi possível excluir.", "error");
            });
        }
      });
    },
  },
};
</script>
